import {
  presignFileUpload,
  confirmFileUpload,
} from "@/lib/api/handlers/AttachmentHandling";
import {
  presignTemplateFileUploadHandler,
  confirmTemplateFileUploadHandler,
} from "@/lib/api/handlers/TemplateAttachmentHandling";
import { ServerError } from "@/lib/api/errors/ErrorHandler";
import { AttachmentFile } from "@/types/Attachment";

/**
 * FA10 — content-type allowlist mirrored from
 * {@code AttachmentValidation.ALLOWED_CONTENT_TYPES} on the backend.
 * Keep in sync — backend rejects any other content-type with 400 at
 * presign time, so a mismatch lets the user fail late instead of being
 * told up-front.
 */
export const ALLOWED_ATTACHMENT_CONTENT_TYPES = new Set<string>([
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "application/zip",
]);

export function isAllowedAttachmentContentType(contentType: string | undefined | null): boolean {
  if (!contentType) return false;
  return ALLOWED_ATTACHMENT_CONTENT_TYPES.has(contentType.toLowerCase());
}

/**
 * FA8 — discriminated-union return so callers can render per-failure-
 * mode UX. Three failure stages map to distinct error codes:
 *   - {@code presign}: backend rejected the presign (size cap, bad
 *     content-type, missing event/attachment, plugin disabled, etc.)
 *   - {@code upload}: S3 PUT failed (presign URL expired, network
 *     drop, content-length mismatch). The pending row will Mongo-TTL
 *     and the orphan-cleanup task GCs the (possibly partially-PUT) S3
 *     object — see BA12 closure in the audit.
 *   - {@code confirm}: backend rejected the confirm (presignNotFound,
 *     presignedObjectMissing, fileSizeMismatch — see BA9/BA29/BA31).
 */
export type UploadResult =
  | { ok: true; file: AttachmentFile }
  | { ok: false; stage: "presign" | "upload" | "confirm"; message?: string };

/**
 * Shared presign → S3 PUT → confirm driver. The event and template
 * flows differ only in which handlers mint the presign/confirm (and
 * whether guestId exists — template files are never guest-scoped), so
 * both entry points delegate here with the API calls injected.
 */
async function runPresignedUpload(
  file: File,
  presign: (data: { fileName: string; contentType: string; fileSize: number }) =>
    Promise<{ uploadUrl: string; s3Key: string } | ServerError>,
  confirm: (data: { s3Key: string; fileName: string; fileSize: number; contentType: string }) =>
    Promise<AttachmentFile | ServerError>,
): Promise<UploadResult> {
  const contentType = file.type || "application/octet-stream";
  const presignResult = await presign({
    fileName: file.name,
    contentType,
    fileSize: file.size,
  });
  if (!presignResult || "isServerError" in presignResult) {
    return {
      ok: false,
      stage: "presign",
      message: presignResult && "message" in presignResult ? presignResult.message : undefined,
    };
  }

  let uploadResponse: Response;
  try {
    uploadResponse = await fetch(presignResult.uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": contentType },
    });
  } catch (err) {
    return {
      ok: false,
      stage: "upload",
      message: err instanceof Error ? err.message : "S3 upload failed",
    };
  }
  if (!uploadResponse.ok) {
    return { ok: false, stage: "upload", message: `S3 returned ${uploadResponse.status}` };
  }

  const confirmResult = await confirm({
    s3Key: presignResult.s3Key,
    fileName: file.name,
    fileSize: file.size,
    contentType,
  });
  if (!confirmResult || "isServerError" in confirmResult) {
    return {
      ok: false,
      stage: "confirm",
      message: confirmResult && "message" in confirmResult ? confirmResult.message : undefined,
    };
  }
  return { ok: true, file: confirmResult as AttachmentFile };
}

/**
 * Drives the presign → S3 PUT → confirm chain for a single file.
 * Returns a discriminated union so the caller can distinguish failure
 * modes and surface per-stage UX.
 */
export async function uploadFilePresigned(
  eventId: string,
  attachmentId: string,
  file: File,
  guestId?: string,
): Promise<UploadResult> {
  return runPresignedUpload(
    file,
    (data) => presignFileUpload(eventId, attachmentId, { ...data, guestId }),
    (data) => confirmFileUpload(eventId, attachmentId, { ...data, guestId }),
  );
}

/**
 * Template-attachment variant of {@link uploadFilePresigned}. Same
 * three-stage chain against /api/v1/template/attachments; template
 * file uploads are never guest-scoped, so no guestId parameter.
 */
export async function uploadTemplateFilePresigned(
  templateId: string,
  attachmentId: string,
  file: File,
): Promise<UploadResult> {
  return runPresignedUpload(
    file,
    (data) => presignTemplateFileUploadHandler(templateId, attachmentId, data),
    (data) => confirmTemplateFileUploadHandler(templateId, attachmentId, data),
  );
}
